/**
 * A ANÁLISE CRÍTICA COMPARATIVA — por regras (lote de 09/10/2026, item 7). Puro, sem DOM.
 *
 * Decisão do usuário (09/10/2026): análise por REGRAS agora; IA fica para depois. Sem custo,
 * a mesma resposta para os mesmos números, e cada frase aponta o número de onde saiu
 * (`frase.ts`). A mesma análise alimenta o relatório e a síntese da Comparação na tela —
 * duas análises diferentes sobre os mesmos números criariam a dúvida de qual está certa.
 *
 * AS REGRAS DO RANKING (Catálogo de objetivos, docs/Catalogo-Objetivos-ModoGuiado.md):
 *   1. viabilidade é o PISO: viável sem ressalvas (verde) > viável com ressalvas (amarelo)
 *      > não viável (vermelho). Inviável nunca é recomendado havendo um viável;
 *   2. dentro do mesmo patamar, o OBJETIVO escolhido na hora de gerar (os do modo Guiado);
 *   3. empate no objetivo (o mesmo degrau de uma resolução declarada): mais critérios atendidos →
 *      maior folga do critério mais apertado → menos recursos próprios → nome → id.
 *      Determinístico: a ordem nunca depende da ordem em que os cenários chegaram.
 * Cenário com DADOS INSUFICIENTES (faltam campos obrigatórios) fica fora do ranking — sem
 * isso, um cenário zerado "venceria" em menor aporte ou menor LGC.
 */
import { CONSTANTES, frase, juntar, n, nome, ref } from './frase';
import type { CampoValor, Formato, Frase, Parte } from './frase';
import { ALAVANCAS, ALAVANCA_PJ_LIMITE, CRIT_NOMES } from './textos';
import type { ChaveObjetivo, Objetivo, Veredito } from './textos';
import { nomeDaFolga } from './modelo';
import type { ModeloCenario, ModeloRelatorio, TravaPJ } from './modelo';

/* ============================================================================
 * Objetivo → métrica
 * ========================================================================== */

export interface RegraObjetivo {
  campo: CampoValor;
  sentido: 'max' | 'min';
  formato: Formato;
  /** como a métrica é chamada na frase (o mesmo rótulo do cartão do Guiado) */
  rotulo: string;
  /**
   * A RESOLUÇÃO da comparação: o valor é ARREDONDADO nela antes de comparar (`quantizar`), e
   * valores que caem no mesmo degrau EMPATAM (1 real; 1 unidade; praticamente exato nas
   * razões). Não é uma janela deslizante ("diferença menor que meio real empata"): com janela,
   * A≈B e B≈C não implicam A≈C, a relação deixa de ser transitiva e o `sort` passa a depender da
   * ordem em que os cenários chegaram — três cenários a R$ 0,40 um do outro davam três
   * recomendados diferentes conforme a ordem. Em reais, o degrau é o mesmo do número impresso
   * (moeda sem centavos): "empata" passa a coincidir com o que a frase mostra.
   */
  tolerancia: number;
}

export const REGRA_OBJETIVO: Record<ChaveObjetivo, RegraObjetivo> = {
  recProprios: { campo: 'recursosProprios', sentido: 'min', formato: 'moeda', rotulo: 'Recursos próprios comprometidos', tolerancia: 0.5 },
  lgc: { campo: 'saldoLGC', sentido: 'max', formato: 'moeda', rotulo: 'Reserva de LGC livre', tolerancia: 0.5 },
  resultado: { campo: 'resultado', sentido: 'max', formato: 'moeda', rotulo: 'Resultado absoluto (VGV − CTE)', tolerancia: 0.5 },
  folga: { campo: 'folgaMin', sentido: 'max', formato: 'folga', rotulo: 'Folga do critério mais apertado', tolerancia: 1e-9 },
  comercial: { campo: 'unidadesExigidas', sentido: 'min', formato: 'unidades', rotulo: 'Unidades exigidas (vendidas + financiadas)', tolerancia: 0.5 },
  alavancagem: { campo: 'alavancagem', sentido: 'max', formato: 'vezes', rotulo: 'Alavancagem (PJ por real próprio)', tolerancia: 1e-9 },
};

const TOL_REAIS = 0.5;

/**
 * O valor que ORDENA. A alavancagem sem denominador é tratada de propósito: com PJ e nenhum
 * recurso próprio ela é ilimitada (vai à frente); sem PJ e sem recurso próprio ela não tem
 * valor (vai para o fim do patamar). Nunca um `Infinity` vazando para a tela.
 */
function chaveDoObjetivo(c: ModeloCenario, k: ChaveObjetivo): number {
  const r = REGRA_OBJETIVO[k];
  if (k === 'alavancagem' && c.semRecursosProprios) {
    return (c.valores.PJ ?? 0) > TOL_REAIS ? Infinity : -Infinity;
  }
  const v = c.valores[r.campo];
  if (v === null) return r.sentido === 'max' ? -Infinity : Infinity;
  return v;
}

/** A métrica como PARTE de frase: número com fonte, ou o texto do caso sem denominador. */
export function metricaComoParte(c: ModeloCenario, k: ChaveObjetivo): Parte {
  const r = REGRA_OBJETIVO[k];
  if (k === 'alavancagem' && c.semRecursosProprios) {
    return (c.valores.PJ ?? 0) > TOL_REAIS ? 'ilimitada (nenhum recurso próprio)' : 'sem valor (nem PJ nem recurso próprio)';
  }
  const v = c.valores[r.campo];
  return v === null ? '—' : n(v, r.formato, ref(c.id, r.campo));
}

/* ============================================================================
 * Ranking
 * ========================================================================== */

export type Desempate = 'viabilidade' | 'objetivo' | 'criterios' | 'folga' | 'recursos' | 'nome';

const PATAMAR: Record<string, number> = { green: 0, yellow: 1, red: 2 };

/**
 * O valor no DEGRAU da resolução `tol` (ver `RegraObjetivo.tolerancia`): com tol = 0,5 é o real
 * inteiro mais próximo. Comparar degraus com igualdade EXATA é transitivo por construção — a
 * ordem lexicográfica de pré-ordens totais, fechada pelo id, é uma ordem estrita de verdade.
 * Os infinitos (alavancagem sem denominador, valor ausente) passam intactos e se comparam certo.
 */
function quantizar(v: number, tol: number): number {
  return Number.isFinite(v) ? Math.round(v / (2 * tol)) : v;
}

const TOL_RAZAO = 1e-9;

/** Negativo = `a` vem antes. Devolve também QUEM decidiu (para a frase do "por quê"). */
export function comparar(a: ModeloCenario, b: ModeloCenario, k: ChaveObjetivo): { r: number; por: Desempate } {
  const pa = PATAMAR[a.cor]!, pb = PATAMAR[b.cor]!;
  if (pa !== pb) return { r: pa - pb, por: 'viabilidade' };
  const regra = REGRA_OBJETIVO[k];
  const va = quantizar(chaveDoObjetivo(a, k), regra.tolerancia), vb = quantizar(chaveDoObjetivo(b, k), regra.tolerancia);
  if (va !== vb) {
    const d = va > vb ? 1 : -1;
    return { r: regra.sentido === 'max' ? -d : d, por: 'objetivo' };
  }
  const na = a.valores.nCriterios ?? 0, nb = b.valores.nCriterios ?? 0;
  if (na !== nb) return { r: nb - na, por: 'criterios' };
  const fa = quantizar(a.valores.folgaMin ?? -Infinity, TOL_RAZAO), fb = quantizar(b.valores.folgaMin ?? -Infinity, TOL_RAZAO);
  if (fa !== fb) return { r: fa > fb ? -1 : 1, por: 'folga' };
  const ra = quantizar(a.valores.recursosProprios ?? 0, TOL_REAIS), rb = quantizar(b.valores.recursosProprios ?? 0, TOL_REAIS);
  if (ra !== rb) return { r: ra < rb ? -1 : 1, por: 'recursos' };
  const dn = a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
  if (dn !== 0) return { r: dn, por: 'nome' };
  return { r: a.id < b.id ? -1 : a.id > b.id ? 1 : 0, por: 'nome' };
}

/**
 * O que separou um cenário do seguinte, como a linha do ranking escreve. A preposição mora
 * AQUI, já contraída: colada num "por" fixo, a página imprimia "por o objetivo", "por a folga" e
 * "por o nome" — num documento que vai para a diretoria e o banco.
 */
export const TEXTO_DESEMPATE: Readonly<Record<Desempate, string>> = {
  viabilidade: 'pela viabilidade',
  objetivo: 'pelo objetivo',
  criterios: 'por ter mais critérios atendidos',
  folga: 'pela folga do critério mais apertado',
  recursos: 'por usar menos recursos próprios',
  nome: 'pelo nome (empate em tudo)',
};

export function textoDoDesempate(por: Desempate): string {
  return `à frente do seguinte ${TEXTO_DESEMPATE[por]}`;
}

export interface PosicaoRanking {
  id: string;
  nome: string;
  posicao: number;
  veredito: Veredito;
  viavel: boolean;
  metrica: Parte;
  /** o que separou este do PRÓXIMO da lista (null no último) */
  decidido: Desempate | null;
}

export function ordenar(cenarios: readonly ModeloCenario[], k: ChaveObjetivo): ModeloCenario[] {
  return cenarios.filter((c) => c.suficiente).sort((a, b) => comparar(a, b, k).r);
}

const viavel = (c: ModeloCenario): boolean => c.cor !== 'red';

/* ============================================================================
 * A análise
 * ========================================================================== */

export interface Analise {
  objetivo: Objetivo;
  regra: RegraObjetivo;
  ranking: PosicaoRanking[];
  /** `null` = nenhum cenário viável (ou nenhum com dados suficientes) */
  recomendadoId: string | null;
  porque: Frase[];
  resumo: Frase[];
  avisos: Frase[];
  tradeoffs: Frase[];
  riscos: Array<{ id: string; nome: string; frases: Frase[] }>;
  recomendacoes: Array<{ id: string; nome: string; itens: Array<{ frase: Frase; alavancas: string[] }> }>;
  notas: Frase[];
}

const totalCriterios = () => n(CONSTANTES.totalCriterios, 'inteiro', { constante: 'totalCriterios' });

function resumoDoCenario(c: ModeloCenario): Frase {
  const versao: Parte[] = c.versaoPublicada > 0 ? [' (', n(c.versaoPublicada, 'versao', ref(c.id, 'versao')), ')'] : [];
  if (!c.suficiente) {
    return frase(nome(c.nome), ...versao, ': dados insuficientes — falta preencher ', c.faltam.join(', '), '. Fica fora do ranking.');
  }
  return frase(nome(c.nome), ...versao, ': ', c.veredito, ' — ', ...fraseDosCriterios(c).partes);
}

/** "5 de 7 critérios atendidos. Não atende: …" — sem o nome (a síntese já o tem no título do cartão). */
export function fraseDosCriterios(c: ModeloCenario): Frase {
  if (!c.suficiente) return frase('Dados insuficientes — falta preencher ', c.faltam.join(', '), '. Fica fora do ranking.');
  const falhas = c.criterios.filter((x) => !x.ok).map((x) => x.nome);
  return frase(
    n(c.valores.nCriterios ?? 0, 'inteiro', ref(c.id, 'nCriterios')), ' de ', totalCriterios(), ' critérios atendidos',
    falhas.length > 0 ? `. Não atende: ${falhas.join(', ')}.` : '.',
  );
}

function porqueDoRecomendado(ord: ModeloCenario[], k: ChaveObjetivo, objetivo: Objetivo): Frase[] {
  const x = ord[0]!;
  const y = ord[1];
  const regra = REGRA_OBJETIVO[k];
  const saida: Frase[] = [frase('Recomendado para «', objetivo.t, '»: ', nome(x.nome), '.')];
  if (y === undefined) {
    saida.push(frase(nome(x.nome), ' é o único cenário com dados suficientes para a comparação.'));
    saida.push(frase(regra.rotulo, ': ', metricaComoParte(x, k), '.'));
    return saida;
  }
  const { por } = comparar(x, y, k);
  const nViaveis = ord.filter(viavel).length;
  if (por === 'viabilidade') {
    saida.push(nViaveis === 1
      ? frase(nome(x.nome), ' é o único cenário viável: cumpre todos os critérios obrigatórios.')
      : frase(nome(x.nome), ' é o único viável sem ressalvas; os demais têm ressalva ou não fecham.'));
    saida.push(frase(regra.rotulo, ' de ', nome(x.nome), ': ', metricaComoParte(x, k), '.'));
  } else if (por === 'objetivo') {
    saida.push(frase(
      'Entre os de mesmo patamar de viabilidade, ', nome(x.nome), ' tem a melhor marca em «', regra.rotulo, '»: ',
      metricaComoParte(x, k), ', contra ', metricaComoParte(y, k), ' de ', nome(y.nome), '.',
    ));
  } else {
    const desempate: Parte[] =
      por === 'criterios'
        ? ['mais critérios atendidos (', n(x.valores.nCriterios ?? 0, 'inteiro', ref(x.id, 'nCriterios')), ' contra ',
          n(y.valores.nCriterios ?? 0, 'inteiro', ref(y.id, 'nCriterios')), ')']
        : por === 'folga'
          ? ['a maior folga do critério mais apertado (', n(x.valores.folgaMin ?? 0, 'folga', ref(x.id, 'folgaMin')), ' contra ',
            n(y.valores.folgaMin ?? 0, 'folga', ref(y.id, 'folgaMin')), ')']
          : por === 'recursos'
            ? ['menos recursos próprios (', n(x.valores.recursosProprios ?? 0, 'moeda', ref(x.id, 'recursosProprios')), ' contra ',
              n(y.valores.recursosProprios ?? 0, 'moeda', ref(y.id, 'recursosProprios')), ')']
            : ['a ordem alfabética do nome — os números empatam em tudo o que o ranking olha'];
    saida.push(frase(
      nome(x.nome), ' empata com ', nome(y.nome), ' em «', regra.rotulo, '» (', metricaComoParte(x, k), '); o desempate foi ', ...desempate, '.',
    ));
  }
  return saida;
}

/* ---------------------------------------------------------------- trade-offs */

interface Dimensao {
  campo: CampoValor;
  /** `true` = menos é melhor */
  menorMelhor: boolean;
  formato: Formato;
  /** [texto antes do número, texto depois] quando o 1º cenário tem MAIS / MENOS */
  mais: [string, string];
  menos: [string, string];
}

const DIMENSOES: readonly Dimensao[] = [
  { campo: 'aporte', menorMelhor: true, formato: 'moeda', mais: ['usa ', ' a mais de aporte'], menos: ['usa ', ' a menos de aporte'] },
  { campo: 'terreno', menorMelhor: true, formato: 'moeda', mais: ['compromete ', ' a mais de terreno em garantia'], menos: ['compromete ', ' a menos de terreno em garantia'] },
  { campo: 'tomadaLGC', menorMelhor: true, formato: 'moeda', mais: ['consome ', ' a mais de LGC'], menos: ['economiza ', ' de LGC'] },
  { campo: 'unidadesExigidas', menorMelhor: true, formato: 'unidades', mais: ['exige ', ' a mais na demanda'], menos: ['exige ', ' a menos na demanda'] },
  { campo: 'resultado', menorMelhor: false, formato: 'moeda', mais: ['tem ', ' a mais de resultado'], menos: ['tem ', ' a menos de resultado'] },
];

function tradeoff(a: ModeloCenario, b: ModeloCenario): Frase {
  const custos: Parte[][] = [], ganhos: Parte[][] = [];
  for (const d of DIMENSOES) {
    const va = a.valores[d.campo] ?? 0, vb = b.valores[d.campo] ?? 0;
    const dif = va - vb;
    if (Math.abs(dif) <= TOL_REAIS) continue;
    const temMais = dif > 0;
    const [antes, depois] = temMais ? d.mais : d.menos;
    const parte: Parte[] = [antes, n(Math.abs(dif), d.formato, { dif: temMais ? [ref(a.id, d.campo), ref(b.id, d.campo)] : [ref(b.id, d.campo), ref(a.id, d.campo)] }), depois];
    const bom = d.menorMelhor ? !temMais : temMais;
    (bom ? ganhos : custos).push(parte);
  }
  if (custos.length === 0 && ganhos.length === 0) {
    return frase(nome(a.nome), ' e ', nome(b.nome), ' empatam em aporte, terreno em garantia, LGC consumido, unidades exigidas e resultado.');
  }
  if (custos.length > 0 && ganhos.length > 0) {
    return frase(nome(a.nome), ' ', ...juntar(custos, ', '), ' e, em troca, ', ...juntar(ganhos), ' em relação a ', nome(b.nome), '.');
  }
  if (ganhos.length > 0) {
    return frase(nome(a.nome), ' ', ...juntar(ganhos), ' em relação a ', nome(b.nome), ', sem perder nos outros quesitos comparados.');
  }
  return frase(nome(a.nome), ' ', ...juntar(custos), ' em relação a ', nome(b.nome), ', sem ganhar nos outros quesitos comparados.');
}

/* ---------------------------------------------------------------- riscos */

const DESCRICAO_TRAVA: Record<TravaPJ, (c: ModeloCenario) => Parte[]> = {
  pjLimite: () => ['no PJ limite do cenário'],   /* APC-4 (12/10/2026): desde a 0032 o PJ limite é de cada cenário */
  lgc: (c) => ['na linha de crédito (LGC disponível de ', n(c.valores.lgcDisponivel ?? 0, 'moeda', ref(c.id, 'lgcDisponivel')), ')'],
  normativoVGV: () => ['no teto normativo de ', n(CONSTANTES.tetoVGVPct, 'taxa', { constante: 'tetoVGVPct' }), ' do VGV'],
  normativoCTO: () => ['no teto normativo de ', n(CONSTANTES.tetoCTOPct, 'taxa', { constante: 'tetoCTOPct' }), ' do CTO'],
};

/** Como dizer "quem limitou o PJ" — usado pelo risco e pela tabela de resultados. */
export function quemLimitouComoPartes(c: ModeloCenario): Parte[] {
  const l = c.limitePJ;
  if (l.situacao === 'piso') return ['nenhuma trava — as garantias já cobrem o CTO'];
  if (l.situacao === 'necessidade') return ['nenhuma trava — é o que falta para o CTO'];
  return juntar(l.travas.map((t) => DESCRICAO_TRAVA[t](c)));
}

const FOLGAS: ReadonlyArray<[number, CampoValor]> = [[1, 'folgaVend'], [2, 'folgaFin'], [3, 'folgaLGC'], [4, 'folgaIGH']];

function riscosDoCenario(c: ModeloCenario): Frase[] {
  const v = c.valores, id = c.id;
  const saida: Frase[] = [];
  for (const [i, campo] of FOLGAS) {
    const f = v[campo];
    if (c.criterios[i]!.ok && f !== null && f < CONSTANTES.limiarFolga) {
      saida.push(frase('Folga de ', n(f, 'folga', ref(id, campo)), ' em «', CRIT_NOMES[i]!, '», abaixo de ',
        n(CONSTANTES.limiarFolga, 'folga', { constante: 'limiarFolga' }), ': pouca margem para imprevisto.'));
    }
  }
  const prazo = v.folgaPrazo;
  if (c.criterios[6]!.ok && prazo !== null && prazo <= CONSTANTES.mesesPerto && v.ymNorm !== null) {
    saida.push(prazo === 0
      ? frase('O cumprimento de demanda coincide com o prazo normativo (', n(v.ymNorm, 'mes', ref(id, 'ymNorm')), '): nenhum mês de folga.')
      : frase('O cumprimento de demanda fica a ', n(prazo, 'meses', ref(id, 'folgaPrazo')), ' do prazo normativo (', n(v.ymNorm, 'mes', ref(id, 'ymNorm')), ').'));
  }
  if (c.limitePJ.situacao === 'trava') {
    saida.push(frase('O PJ (', n(v.PJ ?? 0, 'moeda', ref(id, 'PJ')), ') está preso ', ...quemLimitouComoPartes(c),
      ', e as garantias deixam ', n(v.gapCTO ?? 0, 'moeda', ref(id, 'gapCTO')), ' descoberto.'));
  }
  if (c.premissas.terrenoGarantia && (v.terreno ?? 0) > TOL_REAIS) {
    saida.push(frase('O terreno entra como garantia (', n(v.terreno ?? 0, 'moeda', ref(id, 'terreno')), '): fica onerado durante a operação.'));
  }
  return saida;
}

/* ---------------------------------------------------------------- recomendações */

function faltaDoCriterio(c: ModeloCenario, i: number): Frase {
  const v = c.valores, id = c.id;
  const nm = CRIT_NOMES[i]!;
  switch (i) {
    case 0: return frase(nm, ': faltam ', n(v.gapCTO ?? 0, 'moeda', ref(id, 'gapCTO')), ' para cobrir o CTO de ', n(v.CTO ?? 0, 'moeda', ref(id, 'CTO')), '.');
    /* O verbo e o particípio concordam com a diferença, pela MESMA régua do formatador
       (`formatar(..., 'unidades')` escreve "1 unidade" no singular): fixos no plural, a frase
       saía "faltam 1 unidade vendidas" justamente no caso mais comum perto do limite. */
    case 1: {
      const dif = (v.salesMin ?? 0) - (v.vendidas ?? 0), um = dif === 1;
      return frase(nm, um ? ': falta ' : ': faltam ', n(dif, 'unidades', { dif: [ref(id, 'salesMin'), ref(id, 'vendidas')] }),
        um ? ' vendida (tem ' : ' vendidas (tem ', n(v.vendidas ?? 0, 'inteiro', ref(id, 'vendidas')), ', precisa ', n(v.salesMin ?? 0, 'inteiro', ref(id, 'salesMin')), ').');
    }
    case 2: {
      const dif = (v.finMin ?? 0) - (v.financiadas ?? 0), um = dif === 1;
      return frase(nm, um ? ': falta ' : ': faltam ', n(dif, 'unidades', { dif: [ref(id, 'finMin'), ref(id, 'financiadas')] }),
        um ? ' financiada (tem ' : ' financiadas (tem ', n(v.financiadas ?? 0, 'inteiro', ref(id, 'financiadas')), ', precisa ', n(v.finMin ?? 0, 'inteiro', ref(id, 'finMin')), ').');
    }
    case 3: return frase(nm, ': o consumo de LGC de ', n(v.tomadaLGC ?? 0, 'moeda', ref(id, 'tomadaLGC')), ' passa do disponível (',
      n(v.lgcDisponivel ?? 0, 'moeda', ref(id, 'lgcDisponivel')), ') em ',
      n((v.tomadaLGC ?? 0) - (v.lgcDisponivel ?? 0), 'moeda', { dif: [ref(id, 'tomadaLGC'), ref(id, 'lgcDisponivel')] }), '.');
    case 4: return frase(nm, ': a garantia em estoque (', n(v.ighReal ?? 0, 'moeda', ref(id, 'ighReal')), ') está ',
      n((v.ighMin ?? 0) - (v.ighReal ?? 0), 'moeda', { dif: [ref(id, 'ighMin'), ref(id, 'ighReal')] }), ' abaixo do mínimo (',
      n(v.ighMin ?? 0, 'moeda', ref(id, 'ighMin')), ').');
    case 5: return frase(nm, ': a obra em garantia (', n(v.obraGarPct ?? 0, 'taxa', ref(id, 'obraGarPct')), ') está abaixo do mínimo de obra (',
      n(v.minObraPct ?? 0, 'taxa', ref(id, 'minObraPct')), ').');
    default: return frase(nm, ': o cumprimento de demanda (', n(v.ymDesejada ?? 0, 'mes', ref(id, 'ymDesejada')), ') passa do prazo normativo (',
      n(v.ymNorm ?? 0, 'mes', ref(id, 'ymNorm')), ').');
  }
}

function recomendacoesDoCenario(c: ModeloCenario): Array<{ frase: Frase; alavancas: string[] }> {
  const saida: Array<{ frase: Frase; alavancas: string[] }> = [];
  c.criterios.forEach((cr, i) => {
    if (cr.ok) return;
    const lista = [...ALAVANCAS[i]!];
    /* a mesma condição de `pjLimInfo(...).corta` do Guiado: o motor sem o limite dá mais PJ */
    const corta = c.valores.pjSemLimite !== null && c.valores.pjSemLimite > (c.valores.PJ ?? 0) + TOL_REAIS;
    if (i === 0 && corta) lista.unshift(ALAVANCA_PJ_LIMITE);
    saida.push({
      frase: faltaDoCriterio(c, i),
      alavancas: lista.filter((a) => a.k === undefined || !c.travadas.includes(a.k)).map((a) => a.t),
    });
  });
  return saida;
}

/* ---------------------------------------------------------------- notas de método */

function notas(k: ChaveObjetivo): Frase[] {
  const saida: Frase[] = [
    frase('Viabilidade é o piso: um cenário que não cumpre os critérios obrigatórios nunca é recomendado quando há um que cumpre. '
      + 'Entre os viáveis, primeiro os sem ressalvas; depois vale o objetivo escolhido. Empate no objetivo: mais critérios atendidos, '
      + 'depois a maior folga do critério mais apertado, depois menos recursos próprios e, por fim, o nome.'),
    frase('IGH estático: a garantia em estoque na data de cumprimento de demanda, comparada com o mínimo de ',
      n(CONSTANTES.fatorIGH, 'vezes', { constante: 'fatorIGH' }), ' o PJ — o mesmo da tela de Análise de Demanda. A evolução do IGH mês a mês não entra.'),
    /* As notas acima e abaixo não nomeiam TELA nem ABA que só o Admin Hype vê: o relatório circula
       para todos os perfis (e para fora), e desde o item 3 do mesmo lote o Acompanhamento e as abas
       de fluxo são só dele — é o mesmo critério da dica do EL-085, que deixou de citar tela. A de
       cima cita a "Análise de Demanda" (ex-"tela de Premissas", renomeada no lote 1 de 09/10/2026),
       que todo perfil vê. */
    frase('A projeção mês a mês fica fora: as linhas financeiras dela (juros, amortização, rendimento) ainda são provisórias, por isso o relatório '
      + 'não fala de caixa mês a mês nem de primeira liberação.'),
    frase('Os números saem do mesmo motor de cálculo da tela de cada cenário — o mesmo conferido contra a planilha de análise da própria Caixa.'),
    /* 0032 (12/10/2026, D23 aprovado como proposto): o cadastro passou a ser de cada cenário. "Como
       está publicado hoje", e não "da última versão": um cenário que ainda não foi republicado
       depois da migração carrega o cadastro que o empreendimento tinha em 12/10. */
    frase('Cada cenário entra na sua última versão publicada: alterações ainda não publicadas não aparecem. O cadastro de cada cenário '
      + '(custo, terreno, LGC disponível, PJ limite) é o dele, como está publicado hoje; com a tabela «Demanda por unidade» preenchida, '
      + 'VGV, unidades, permutadas, vendidas e financiadas vêm da tabela de cada cenário, como na tela dele.'),
    frase('Folga: vendidas e financiadas contra o mínimo exigido, o LGC contra o disponível e o IGH contra o mínimo; a obra em pontos '
      + 'percentuais e a data em meses. «Garantias ≥ CTO» não tem folga: o PJ fecha a conta. Folga abaixo de ',
      n(CONSTANTES.limiarFolga, 'folga', { constante: 'limiarFolga' }), ' entra como risco.'),
  ];
  if (k === 'alavancagem') {
    saida.push(frase('Alavancagem = PJ por real próprio (aporte + terreno em garantia). Sem recurso próprio nenhum e com PJ, ela é '
      + 'ilimitada e vai à frente; sem PJ e sem recurso próprio, fica sem valor e vai para o fim do seu patamar.'));
  }
  return saida;
}

/**
 * "O mais próximo de fechar" tem régua PRÓPRIA, e não a do objetivo: dentro do vermelho,
 * `comparar` decide pelo OBJETIVO antes de contar critérios — e por ela um cenário com 3 de 7
 * critérios e nenhum recurso próprio passaria à frente de um com 6 de 7 em "comprometer o
 * mínimo". Aqui: mais critérios atendidos, depois a maior folga do critério mais apertado, e por
 * fim a posição no ranking (que guarda o desempate determinístico de `comparar`).
 */
function maisProximoDeFechar(ord: readonly ModeloCenario[]): ModeloCenario {
  return ord.map((c, i) => ({ c, i })).sort((x, y) => {
    const nx = x.c.valores.nCriterios ?? 0, ny = y.c.valores.nCriterios ?? 0;
    if (nx !== ny) return ny - nx;
    const fx = quantizar(x.c.valores.folgaMin ?? -Infinity, TOL_RAZAO), fy = quantizar(y.c.valores.folgaMin ?? -Infinity, TOL_RAZAO);
    if (fx !== fy) return fx > fy ? -1 : 1;
    return x.i - y.i;
  })[0]!.c;
}

/* ---------------------------------------------------------------- tudo junto */

export function analisar(m: ModeloRelatorio): Analise {
  const k = m.objetivo.k;
  const ord = ordenar(m.cenarios, k);
  const ranking: PosicaoRanking[] = ord.map((c, i) => ({
    id: c.id,
    nome: c.nome,
    posicao: i + 1,
    veredito: c.veredito,
    viavel: viavel(c),
    metrica: metricaComoParte(c, k),
    decidido: i + 1 < ord.length ? comparar(c, ord[i + 1]!, k).por : null,
  }));
  const primeiro = ord[0];
  const recomendadoId = primeiro !== undefined && viavel(primeiro) ? primeiro.id : null;

  let porque: Frase[];
  if (primeiro === undefined) {
    porque = [frase('Nenhum cenário tem dados suficientes para a análise: complete os campos obrigatórios e publique.')];
  } else if (recomendadoId === null) {
    const proximo = maisProximoDeFechar(ord);
    porque = [frase('Nenhum cenário cumpre todos os critérios obrigatórios, por isso nenhum é recomendado. O mais próximo de fechar é ',
      nome(proximo.nome), ' (', n(proximo.valores.nCriterios ?? 0, 'inteiro', ref(proximo.id, 'nCriterios')), ' de ', totalCriterios(),
      ' critérios atendidos).')];
    /* A ordem do ranking continua sendo a do OBJETIVO; quando ela não começa pelo mais próximo
       de fechar, dizer — senão a linha "Ordem" logo abaixo pareceria contradizer a frase. */
    if (proximo !== primeiro) {
      porque.push(frase('Pelo objetivo «', m.objetivo.t, '», a ordem começa por ', nome(primeiro.nome),
        ': dentro do mesmo patamar, o objetivo pesa antes da contagem de critérios.'));
    }
  } else {
    porque = porqueDoRecomendado(ord, k, m.objetivo);
  }

  const resumo = m.cenarios.map(resumoDoCenario);

  const avisos = m.cenarios.filter((c) => c.temRascunhoMeu).map((c) =>
    frase('Você tem alterações não publicadas em ', nome(c.nome), ': o relatório usa a última versão publicada',
      ...(c.versaoPublicada > 0 ? [' (', n(c.versaoPublicada, 'versao', ref(c.id, 'versao')), ')'] : []),
      '. Publique o cenário para que elas entrem.'));

  const tradeoffs: Frase[] = [];
  for (let i = 0; i < ord.length; i++) for (let j = i + 1; j < ord.length; j++) tradeoffs.push(tradeoff(ord[i]!, ord[j]!));

  return {
    objetivo: m.objetivo,
    regra: REGRA_OBJETIVO[k],
    ranking,
    recomendadoId,
    porque,
    resumo,
    avisos,
    tradeoffs,
    riscos: ord.map((c) => ({ id: c.id, nome: c.nome, frases: riscosDoCenario(c) })),
    recomendacoes: ord.map((c) => ({ id: c.id, nome: c.nome, itens: recomendacoesDoCenario(c) })),
    notas: notas(k),
  };
}

/** O critério mais apertado de um cenário, como frase (síntese e relatório). */
export function fraseDoMaisApertado(c: ModeloCenario): Frase {
  return frase('Critério mais apertado: «', nomeDaFolga(c.folgaMaisApertada), '», com folga de ',
    n(c.valores.folgaMin ?? 0, 'folga', ref(c.id, 'folgaMin')), '.');
}
